import React, { useEffect, useState } from 'react';
import { ConnectedAccount } from '../types/ConnectedAccount';
import { disconnectAccount, getConnectedAccounts } from '../services/AccountConnectionService';
import SocialButton from '../../components/buttons/SocialButton/SocialButton';
import { isSocialAccountConnected } from '../../utils/socialAccounts';
import { useTranslations } from '../../localization/useTranslations';
import Button from '../../components/buttons/SimpleButton/Button';

interface ConnectedAccountsProps {
  setSnackbarMessage: (message: string) => void;
  setIsSnackbarOpen: (value: boolean) => void;
}

const ConnectedAccounts: React.FC<ConnectedAccountsProps> = ({
  setIsSnackbarOpen,
  setSnackbarMessage: setSnackBarMessage,
}) => {
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[] | null>(null);
  const [disconnectingAccountId, setDisconnectingAccountId] = useState<number | null>(null);

  const translations = useTranslations();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    // Pad the day and month with leading zeros if necessary
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // January is 0!
    const year = date.getFullYear();

    const formattedDate = `${day}.${month}.${year}`;

    return formattedDate;
  };

  const removeConnectedAccount = async (id: number) => {
    if (!id) {
      setSnackBarMessage(
        translations.snackBarMessages.failedToRemoveConnectedAccount || 'Failed to remove connected account.'
      );

      setIsSnackbarOpen(true);
    }

    setDisconnectingAccountId(id);

    try {
      const response = await disconnectAccount(id);

      if (response.status === 'ok' && connectedAccounts) {
        const connectedAccountsCopy = [...connectedAccounts];

        const updatedConnectedAccounts = connectedAccountsCopy.filter((account) => account.id !== id);

        setConnectedAccounts(updatedConnectedAccounts);

        setSnackBarMessage(
          translations.snackBarMessages.successfullyRemovedConnectedAccount || 'Successfully removed connected account.'
        );
        setIsSnackbarOpen(true);
      }
    } catch (error) {
      setSnackBarMessage(
        translations.snackBarMessages.failedToRemoveConnectedAccount || 'Failed to remove connected account.'
      );

      setIsSnackbarOpen(true);
    } finally {
      setDisconnectingAccountId(null);
    }
  };

  const fetchConnectedAccounts = () => {
    getConnectedAccounts()
      .then((response) => {
        if (response.status === 'ok') {
          setConnectedAccounts(response.accounts);
        }
      })
      .catch((error: any) => {
        console.error('Failed to fetch data', error);
      });
  };

  const renderAccountType = (accountType: string | null | undefined): string => {
    if (!accountType) {
      return '';
    }

    const type = accountType.split('_')[0];
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  useEffect(() => {
    fetchConnectedAccounts();
  }, []);

  return (
    <div className="my-settings__section my-settings__section--connected-accounts">
      <div className="my-settings__section-header my-settings__section-header--connected-accounts">
        <h4 className="my-settings__section-title">
          {translations.myAccountSettings.connectedAccounts || 'Connected Accounts'}
        </h4>
      </div>

      <div className="my-settings__buttons-wrapper">
        {!isSocialAccountConnected(connectedAccounts, 'google') && <SocialButton provider="google" size="medium" />}
        {!isSocialAccountConnected(connectedAccounts, 'facebook') && window.country !== 'hu' && (
          <SocialButton provider="facebook" size="medium" />
        )}
      </div>

      {connectedAccounts && connectedAccounts.length > 0 && (
        <table className="my-settings__accounts" cellSpacing="0">
          <thead className="my-settings__table-head">
            <tr className="my-settings__table-header-row">
              <th className="my-settings__table-header basic basic-14">
                {translations.myAccountSettings.service || 'Service'}
              </th>

              <th className="my-settings__table-header basic basic-14">
                {translations.myAccountSettings.account || 'Account'}
              </th>

              <th className="my-settings__table-header basic basic-14">
                {translations.myAccountSettings.connected || 'Connected'}
              </th>
            </tr>
          </thead>

          <tbody className="my-settings__table-body">
            {connectedAccounts?.map((account) => (
              <tr className="my-settings__table-row">
                <td className="my-settings__table-data basic basic-16">
                  <span className="my-settings__table-data-inner service">
                    <span className="my-settings__data-label"></span>
                    {renderAccountType(account.type)}
                  </span>
                </td>

                <td className="my-settings__table-data basic basic-16">
                  <span className="my-settings__data-label-inner account">
                    <span className="my-settings__data-label"></span>
                    {account.email}
                  </span>
                </td>

                <td className="my-settings__table-data basic basic-16">
                  <span className="my-settings__data-label-inner connected-at">
                    <span className="my-settings__data-label"></span>
                    {formatDate(account.updated_at)}
                  </span>
                </td>

                <td className="my-settings__table-data my-settings__table-data--button">
                  <div className="my-settings__table-data-button-wrapper">
                    <Button
                      className="my-settings__disconnect-button disconnect"
                      size="medium"
                      type="secondary"
                      onClick={() => removeConnectedAccount(account.id)}
                      label={
                        disconnectingAccountId === account.id
                          ? translations.myAccount.loading || 'Loading...'
                          : translations.myAccountSettings.disconnect || 'Disconnect'
                      }
                      isDisabled={disconnectingAccountId === account.id}
                    ></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ConnectedAccounts;
